import { Stack } from '@mui/material'
import type { FC } from 'react'
import styled from 'styled-components'

import { ButtonsContainer } from '@/components/IdentityCard/IdentityCard'
import AddAccountantReferee from '@/components/IncomeCard/AccountantReference/Add/index'
import RemoveAccountantReferee from '@/components/IncomeCard/AccountantReference/Remove/index'
import Title from '@/components/IncomeCard/AccountantReference/Title'
import ReferenceOutcomeStatusPill from '@/components/ReferenceOutcomeStatusPill'
import type { Income } from '@/types/Activity'
import type { OutcomeStatus } from '@/types/Outcome'
import { isFinalOutcome } from '@/utils/outcome'
import { hasExpired, hasCompletedBotJourney } from '@/utils/referee'

import AccountantReferenceDetails from './AccountantReferenceDetails'

const NoAccountantDetailsWrapper = styled.div`
  font-weight: bold;
  color: red;
  margin-top: 14px;
  margin-bottom: 14px;
`

interface Props {
  income: Income
  applicantName?: string
  navigateToAccountantReferee: (id: string) => void
  customerId: string
  subjectId: string
  incomeOutcomeStatus?: OutcomeStatus
  isRestrictedReference?: boolean
}

const AccountantReference: FC<Props> = ({
  income,
  applicantName,
  navigateToAccountantReferee,
  customerId,
  subjectId,
  incomeOutcomeStatus,
  isRestrictedReference,
}) => {
  const accountantReference = income.accountantReference
  const hasAddedAccountantDetails = income.hasAddedAccountantDetails

  return (
    <div>
      {accountantReference ? (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Title />
            {!isRestrictedReference && !isFinalOutcome(incomeOutcomeStatus) && (
              <ButtonsContainer>
                <RemoveAccountantReferee
                  customerId={customerId}
                  subjectId={subjectId}
                  refereeId={accountantReference.subjectId}
                />
              </ButtonsContainer>
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            justifyContent="space-between"
          >
            {hasExpired(accountantReference) ? (
              <p>An accountant reference has expired for this application.</p>
            ) : hasCompletedBotJourney(accountantReference) ? (
              <p>An accountant reference has been filled out for this application.</p>
            ) : (
              <p>Waiting for accountant to submit their information.</p>
            )}
            <div>
              <ReferenceOutcomeStatusPill
                referenceOutcomeStatus={accountantReference.outcome.status}
              />
            </div>
          </Stack>
          <AccountantReferenceDetails
            applicantName={applicantName}
            accountantReference={accountantReference}
            navigateToAccountantReferee={navigateToAccountantReferee}
          />
        </Stack>
      ) : (
        <>
          <Title />
          {hasAddedAccountantDetails === false ? (
            <NoAccountantDetailsWrapper>
              This applicant could not provide accountant details
            </NoAccountantDetailsWrapper>
          ) : (
            <p>This income does not have an accountant reference.</p>
          )}

          {!isRestrictedReference && (
            <AddAccountantReferee
              customerId={customerId}
              subjectId={subjectId}
              incomeId={income.id}
            />
          )}
        </>
      )}
    </div>
  )
}

export default AccountantReference
